import {
  FontBoldIcon,
  FontItalicIcon,
  ListBulletIcon,
  Pencil2Icon,
  TextNoneIcon,
} from '@radix-ui/react-icons'
import { type UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tiptap/extension-link'
import { type Editor as TipTapEditor, EditorContent, useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type CreateNoteParams } from 'components/hooks/useCreateNote'
import { Button } from 'slab'

import { Banner } from './Banner'
import { useDrawer } from './Drawer'
import LoadingSpinnerCentered from './LoadingSpinnerCentered'
import { getFormattedDate } from '../../utils'
import { getNotesQueryKey } from '../hooks/useGetNotes'
import { useUpdateNote } from '../hooks/useModifyNote'

export type Note = {
  id: string
  author_name: string
  member_id?: string
  employer_id?: string
  created_at: string
  updated_at: string
  content: string
  can_edit: boolean
  inspect_path?: string
}

export const MenuBar = ({ editor }: { editor?: TipTapEditor }) => {
  if (!editor) {
    return null
  }

  return (
    <div className='flex space-x-1 border-b p-1'>
      {/* Bold Button */}
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className='px-2 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200'
        type='button'
      >
        <FontBoldIcon />
      </button>

      {/* Italic Button */}
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className='px-2 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200'
        type='button'
      >
        <FontItalicIcon />
      </button>

      {/* Strikethrough Button */}
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className='px-2 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200'
        type='button'
      >
        <TextNoneIcon />
      </button>

      {/* Bullet List Button */}
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className='px-2 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200'
        type='button'
      >
        <ListBulletIcon />
      </button>
    </div>
  )
}

const editorIsEmpty = (editor: TipTapEditor) => {
  // TODO: This doesn't support cases where the only content is non-text content,
  // like an image.
  return editor.state.doc.textContent.trim().length === 0
}

export const Editor = ({
  onEditorCreate = () => {},
  onEditorEmptyChange = () => {},
  onEditorUpdate,
  content = '',
  editable = true,
  className = '',
}: {
  onEditorCreate?: (editor: TipTapEditor) => void
  onEditorUpdate?: (editor: TipTapEditor) => void
  onEditorEmptyChange?: (isEmpty: boolean) => void
  content?: string
  editable?: boolean
  className?: string
}) => {
  const [isEmpty, setIsEmpty] = useState(true)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc pl-6',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal pl-6',
          },
        },
        heading: false,
        // Exclude link from StarterKit since we're configuring it separately
        link: false,
      }),
      Link.configure({
        defaultProtocol: 'https',
      }),
    ],
    editorProps: {
      attributes: {
        class: `${className} break-words overflow-wrap-anywhere max-w-full word-break-break-all whitespace-pre-wrap`,
        style: 'width: 100%; max-width: 100%; overflow-wrap: anywhere; word-break: break-word;',
      },
    },
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    content,
    editable,
    onCreate: ({ editor }) => {
      onEditorCreate(editor)
    },
    onUpdate: ({ editor }) => {
      onEditorUpdate?.(editor)
      const isNowEmpty = editorIsEmpty(editor)
      if (isEmpty !== isNowEmpty) {
        setIsEmpty(isNowEmpty)
        onEditorEmptyChange(isNowEmpty)
      }
    },
  })

  return <EditorContent editor={editor} />
}

const ErrorBanner = ({ error }: { error: string }) => {
  return (
    <Banner type='critical' titleText='Unable to save note'>
      <p>{error}</p>
    </Banner>
  )
}

const EditedIndicator = ({ note }: { note: Note }) => {
  if (note.updated_at === note.created_at) {
    return <></>
  }

  const displayText = `Edited ${getFormattedDate(note.updated_at, {
    hour: 'numeric',
    minute: 'numeric',
  })}`

  if (note.inspect_path) {
    return (
      <a href={note.inspect_path} className='text-xs'>
        {displayText}
      </a>
    )
  }

  return <span className='text-xs text-gray-500'>{displayText}</span>
}

const NoteItem = ({ note }: { note: Note }) => {
  const queryClient = useQueryClient()
  const editorRef = useRef<TipTapEditor>()
  const [isEditing, setIsEditing] = useState(false)
  const [contentEdited, setContentEdited] = useState(false)
  const [editorIsEmpty, setEditorIsEmpty] = useState(true)

  const updateNoteMutation = useUpdateNote({
    noteId: note.id,
    memberId: note.member_id,
    employerId: note.employer_id,
  })

  const updateEditState = (editable: boolean) => {
    editorRef.current?.setEditable(editable)
    setIsEditing(editable)

    if (editable) {
      editorRef.current?.chain().focus('end').run()
    }
  }

  const onClickCancel = () => {
    updateEditState(false)
    editorRef.current?.commands.setContent(note.content)
  }

  const onClickSave = () => {
    if (!editorRef.current) {
      return
    }

    updateEditState(false)
    updateNoteMutation.mutate(
      { content: editorRef.current.getHTML() },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getNotesQueryKey({ memberId: note.member_id }),
          })
        },
      }
    )
  }

  return (
    <div className='rounded-lg bg-gray-100 p-4 space-y-4 w-full max-w-full overflow-hidden break-words' key={note.id}>
      <div className='flex justify-between items-center gap-1'>
        <div className='space-x-4 min-w-0 flex-1'>
          <span className='font-medium'>{note.author_name}</span>
          <span>{getFormattedDate(note.created_at)}</span>
        </div>
        {note.can_edit && (
          <div
            onClick={() => updateEditState(true)}
            className='cursor-pointer text-thatch-purple-primary hover:text-thatch-purple-hover flex-shrink-0'
          >
            <Pencil2Icon className='w-5 h-5' />
          </div>
        )}
      </div>
      {updateNoteMutation.isError && (
        <ErrorBanner error={updateNoteMutation.error.message} />
      )}
      <div
        className={clsx({
          'border rounded-lg overflow-hidden': isEditing,
        })}
      >
        {isEditing && <MenuBar editor={editorRef.current} />}
        <div className={clsx({ 'bg-white': isEditing })}>
          <div className='w-full max-w-full overflow-hidden'>
            <Editor
              className={clsx('rounded-lg outline-none max-h-60 overflow-y-auto w-full', {
                'p-2': isEditing,
              })}
              onEditorCreate={(editor) => {
                editorRef.current = editor
              }}
              onEditorUpdate={(editor) => {
                setContentEdited(editor.getHTML() !== note.content)
              }}
              onEditorEmptyChange={(isEmpty) => {
                setEditorIsEmpty(isEmpty)
              }}
              content={note.content}
              editable={isEditing}
            />
          </div>
          {isEditing && (
            <div className='flex justify-end gap-2 p-2'>
              <Button
                variant='tertiary-deemphasized'
                type='button'
                onClick={onClickCancel}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={onClickSave}
                disabled={editorIsEmpty || !contentEdited}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className='flex justify-end'>
        <EditedIndicator note={note} />
      </div>
    </div>
  )
}

type NotesProps = {
  error: GrapeAPIErrorType | null
  isLoading: boolean
  createNoteMutation: UseMutationResult<Note, GrapeAPIErrorType, CreateNoteParams, unknown>
  displayExistingNotes?: boolean
  notes: Note[]
  queryKey: (string | undefined)[]
}

const Notes = ({
  error,
  isLoading,
  displayExistingNotes,
  createNoteMutation,
  notes,
  queryKey,
}: NotesProps) => {
  const editorRef = useRef<TipTapEditor>()
  const [editorIsEmpty, setEditorIsEmpty] = useState(true)
  const queryClient = useQueryClient()
  const { getDraftContent, setDraftContent, clearDraftContent, cleanupOldDrafts } = useDrawer()

  // Create a unique key for this Notes instance based on the queryKey
  const draftKey = queryKey.filter(Boolean).join('-')

  // Check if there's existing draft content
  const existingDraft = getDraftContent(draftKey)
  const hasDraft = Boolean(existingDraft && existingDraft.trim())

  // Clear draft manually
  const handleClearDraft = useCallback(() => {
    clearDraftContent(draftKey)
    editorRef.current?.commands.clearContent(true)
  }, [clearDraftContent, draftKey])

  // Clean up old drafts on mount (older than 24 hours)
  useEffect(() => {
    cleanupOldDrafts()
  }, [cleanupOldDrafts])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (!editorRef.current) {
      return
    }

    createNoteMutation.mutate({
      content: editorRef.current.getHTML(),
    }, {
      onSuccess: (note) => {
        editorRef.current?.commands.clearContent(true)
        // Clear draft content after successful submission
        clearDraftContent(draftKey)
        queryClient.setQueryData(queryKey, (cachedNotes: Note[] | undefined) => {
          return [note, ...(cachedNotes ?? [])]
        })
      }
    })
  }

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  return (
    <div className='grid space-y-4 w-full max-w-full'>
      {error && <ErrorBanner error={error.message} />}
      <form onSubmit={(e) => handleSubmit(e)}>
        <div className='flex flex-col border rounded-lg bg-gray-100 gap-2 flex-grow-0'>
          <MenuBar editor={editorRef.current} />
          <div className='w-full max-w-full overflow-hidden'>
            <Editor
              onEditorCreate={(editor) => {
                editorRef.current = editor
                // Restore draft content when editor is created
                const draftContent = getDraftContent(draftKey)
                if (draftContent) {
                  editor.commands.setContent(draftContent)
                  // Update empty state after restoring content
                  const isEmpty = editor.state.doc.textContent.trim().length === 0
                  setEditorIsEmpty(isEmpty)
                }
              }}
              onEditorUpdate={(editor) => {
                // Save draft content as user types
                const content = editor.getHTML()
                const isEmpty = editor.state.doc.textContent.trim().length === 0

                if (isEmpty) {
                  // Clear draft if editor is empty
                  clearDraftContent(draftKey)
                } else {
                  // Save draft content
                  setDraftContent(draftKey, content)
                }
              }}
              onEditorEmptyChange={(isEmpty: boolean) => setEditorIsEmpty(isEmpty)}
              className='min-h-20 max-h-40 overflow-y-auto px-2 pb-2 outline-none w-full'
            />
          </div>
        </div>
        <div className='grid justify-items-end mt-2 w-full'>
          <div className='flex gap-2 items-center'>
            {hasDraft && !editorIsEmpty && (
              <Button
                type='button'
                variant='tertiary-deemphasized'
                onClick={handleClearDraft}
              >
                Clear Draft
              </Button>
            )}
            <Button
              type='submit'
              variant='primary'
              disabled={editorIsEmpty || createNoteMutation.isPending}
            >
              Publish
            </Button>
          </div>
        </div>
      </form>
      {displayExistingNotes &&
        notes.map((note: Note) => <NoteItem key={note.id} note={note} />)}
    </div>
  )
}

export default Notes
